import type { AiCategoryDefinition } from "@/store/slices/categories/categoriesSlice";
import { isAiVisibilityModule } from "@/utils/aiVisibility/isAiVisibilityModule";

interface BrandValue {
  brandId: number | string;
  name: string;
  displayName: string;
  logo: string;
}

interface Brand {
  id: number | string;
  name: string;
  logo: string;
  displayName: string;
  position: number | null;
  color: string | null;
  marketIds: number[];
  reputationMarketIds?: number[];
  type?: string;
  brandValues?: BrandValue[];
  isActive?: boolean;
  // Brand set specific fields
  description?: string;
  ulid?: string | null;
  weighted?: boolean | null;
  members?: { id: number; weight: number; logo?: string | null; name?: string }[];
}
export const prepareBrandData = (group: any, brandsLookup: any): Brand[] => {
  const brandsData: any = group?.brands || [];
  const brandLookups = (brandsLookup || {}) as {
    [key: string]: {
      id: number | string;
      name: string;
      displayName: string;
      logo: string;
      color: string;
      type?: string;
    };
  };

  return brandsData.map((brand: any) => {
    const isBrandSet = brand?.type === "BRAND_SET";
    // For brand sets, use ulid as lookup key; for regular brands, use brandId
    const lookupKey = isBrandSet ? brand.ulid : String(brand.id);
    const lookup = brandLookups[lookupKey];
    const reputationMarketIds =
      brand.markets
        ?.filter((market: any) => market?.reputationDataAvailable === true)
        ?.map((market: any) => market.marketId) || [];

    const baseData: Brand = {
      id: isBrandSet ? brand.ulid : brand.id,
      name: brand?.displayName || brand?.name,
      logo: lookup?.logo || "",
      displayName: brand.displayName || brand.name,
      position: brand.order,
      color: brand.color || lookup?.color,
      marketIds: brand.markets?.map((market: any) => market.marketId),
      reputationMarketIds,
      type: brand?.type || "BRAND",
      brandValues: Array.isArray(brand?.brandValues) ? brand.brandValues : [],
    };

    // Add brand set specific fields
    if (isBrandSet) {
      baseData.ulid = brand.ulid;
      // Description must round-trip into the store so Edit/Clone pre-fill correctly.
      baseData.description = brand.description ?? "";
      // Prefer the API's authoritative `weighted` flag. Unweighted sets are persisted
      // with weight:1 for every member, so deriving from `members.every(w !== null)`
      // wrongly marks them weighted on reload. Fall back to "any member weight !== 1".
      baseData.weighted =
        typeof brand.weighted === "boolean"
          ? brand.weighted
          : (brand.members?.some(
              (ele: { weight?: number | null }) => (ele.weight ?? 1) !== 1,
            ) ?? false);
      baseData.members = (brand.members || []).map((member: any) => ({
        id: member.id,
        weight: member.weight ?? 1,
        logo: member.logo || brandLookups[String(member.id)]?.logo || null,
        name:
          brandLookups[String(member.id)]?.displayName ||
          brandLookups[String(member.id)]?.name ||
          `Brand ${member.id}`,
      }));
    }
    return baseData;
  });
};

export const prepareBrandsMappingData = (brandsOrderMapping: any) => {
  const result: Record<string, (number | string)[]> = {};
  brandsOrderMapping?.forEach((value: any) => {
    if (value?.marketId) {
      result[value.marketId] = (value?.orderedBrandIds ?? []).map(
        (id: number | string) => {
          if (typeof id === 'string') {
            const parsed = Number(id);
            return !isNaN(parsed) ? parsed : id;
          }
          return id;
        }
      );
    }
  });
  return result;
};

export const prepareMarketData = (markets: any, marketLookup: any): any => {
  const brandMarketLookup = markets?.reduce((acc: any, curr: any) => {
    return {
      ...acc,
      [curr.marketId]: curr,
    };
  }, {});
  const result: any = [];
  Object.values(marketLookup)?.forEach((market: any) => {
    result.push({
      id: market?.id,
      name: market?.displayName || market?.name,
      region: market?.region,
      identity: market?.code,
      activeContractsCount: 0,
      position: brandMarketLookup?.[market?.id]?.order,
      color: market?.color,
      groupName:
        market?.region === "International" ? market?.region : market?.name,
      lastWeightedAt: market?.lastWeightedAt,
    });
  });

  const marketGroup = result?.reduce((acc: any, curr: any) => {
    if (acc[curr?.groupName]) {
      return {
        ...acc,
        [curr.groupName]: {
          ...acc[curr.groupName],
          marketIds: [...acc[curr?.groupName]?.marketIds, curr?.id],
        },
      };
    }
    return {
      ...acc,
      [curr.groupName]: {
        id: curr?.groupName,
        name: curr?.groupName,
        marketIds: [curr?.id],
      },
    };
  }, {});

  const groupList = Object.values(marketGroup).sort((a: any, b: any) =>
    a.name.localeCompare(b.name),
  );

  return {
    markets: result,
    groups: groupList,
  };
};

// Wire shape from `bootstrap/v1/unified`'s `clients[0].groups[0].aiCategories`: brand membership
// comes as `brands: { id, ulid?, type }[]`, but every downstream consumer
// (CategoryDropdown, brandBelongsToCategory, selectionListener) keys off
// `AiCategoryDefinition.brandIds` — flatten to ids here so the wire shape
// never leaks past this boundary. Brand sets are keyed by ULID in the brands
// store, so they must contribute their ULID rather than their numeric id.
export const prepareCategoriesData = (categories: any): AiCategoryDefinition[] => {
  if (!Array.isArray(categories)) return [];
  return categories.map((category: any) => ({
    id: category?.id,
    name: category?.name,
    description: category?.description,
    status: category?.status,
    brandIds: (category?.brands ?? [])
      .map((brand: any) =>
        brand?.type === "BRAND_SET" ? brand?.ulid : brand?.id,
      )
      .filter(
        (id: any) =>
          typeof id === "number" || (typeof id === "string" && id !== ""),
      ),
  }));
};

export function prepareFeatureStatusData(currData: any) {
  const result: any = {};

  currData?.modules?.map((module: any) => {
    if (module?.key) {
      // Unified module key is `ai_visibility`; FE featureStatus / routes use
      // `aivisibility` with By Brand + By Model tabs.
      if (isAiVisibilityModule(module.key)) {
        result["aivisibility"] = {
          bybrand: !!module?.enabled,
          bymodel: !!module?.enabled,
        };
        return;
      }
      if (module?.enabled) {
        result[module.key === "brand-visibility" ? "visibility" : module.key] =
        {
          bysegment: true,
          bybrand: true,
          bymarket: true,
        };
      } else {
        result[module.key === "brand-visibility" ? "visibility" : module.key] =
        {
          bysegment: false,
          bybrand: false,
          bymarket: false,
        };
      }
    }
  });

  const reportBuilder = currData?.features?.find(
    (feature: any) => feature?.key === "report.builder",
  );
  const exclusiveQuestions = currData?.features?.find(
    (feature: any) => feature?.key === "exclusive.questions",
  );
  const legacyScore = currData?.features?.find(
    (feature: any) => feature?.key === "rq.scoreType.legacyScore",
  );
  const meanScore = currData?.features?.find(
    (feature: any) => feature?.key === "rq.scoreType.meanScore",
  );
  const aiAssistant = currData?.features?.find(
    (feature: any) => feature?.key === "ai.assistant",
  );

  const brandSets = currData?.features?.find(
    (feature: any) => feature?.key === "brandset",
  );

  if (reportBuilder && reportBuilder?.enabled) {
    result["reports"] = {
      enabled: true,
    };
  } else {
    result["reports"] = {
      enabled: false,
    };
  }

  if (exclusiveQuestions && exclusiveQuestions?.enabled) {
    result["exclusivequestions"] = {
      bybrand: true,
      bysegment: true,
    };
  }

  if (legacyScore && legacyScore?.enabled) {
    result["legacyScore"] = {
      enabled: true,
    };
  } else {
    result["legacyScore"] = {
      enabled: false,
    };
  }

  if (meanScore && meanScore?.enabled) {
    result["meanScore"] = {
      enabled: true,
    };
  } else {
    result["meanScore"] = {
      enabled: false,
    };
  }

  if (aiAssistant && aiAssistant?.enabled) {
    result["aiAssistant"] = {
      enabled: true,
    };
  } else {
    result["aiAssistant"] = {
      enabled: false,
    };
  }

  if (brandSets && brandSets?.enabled) {
    result["brandSet"] = {
      enabled: true,
    };
  } else {
    result["brandSet"] = {
      enabled: false,
    };
  }

  return result;
}
